import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import useApi from "../../hooks/useApi";
import "./VendorBookings.css"; // CSS module ke jagah regular CSS import

const VendorBookings = () => {
  const { bearerAuth } = useApi();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [showFilters, setShowFilters] = useState(false); // Mobile filters toggle

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const params = {
        ...(status !== "" ? { status } : {}),
        ...(dateFrom ? { date_from: dateFrom } : {}),
        ...(dateTo ? { date_to: dateTo } : {}),
      };

      const res = await bearerAuth("vendor/service-bookings", "GET", params);
      if (res?.data?.status) {
        setRows(res.data.data || []);
      } else {
        setRows([]);
      }
    } catch (err) {
      console.error("Error fetching vendor bookings:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusLabel = (val) => {
    const s = Number(val);
    if (s === 1) return "Completed";
    if (s === 2) return "Cancelled";
    return "Pending";
  };

  const statusVariant = (val) => {
    const s = Number(val);
    if (s === 1) return "success";
    if (s === 2) return "danger";
    return "warning";
  };

  const updateStatus = async (id, nextStatus) => {
    setUpdatingId(id);
    try {
      await bearerAuth(`vendor/service-bookings/${id}/status`, "POST", { status: nextStatus });
      await fetchBookings();
    } catch (err) {
      console.error("Error updating booking status:", err);
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredRows = useMemo(() => rows, [rows]);
  const earningsSummary = useMemo(() => {
    const currency = rows?.[0]?.currency || "INR";
    const total = filteredRows.reduce((sum, row) => {
      const payout = row.vendor_payout ?? (Number(row.total || 0) - Number(row.admin_commission || 0));
      return sum + Number(payout || 0);
    }, 0);
    return { currency, total };
  }, [filteredRows, rows]);
  
  const formatDate = (val) => {
    if (!val) return "-";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return String(val);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };
  
  const formatTime = (val) => {
    if (!val) return "-";
    if (/^\d{2}:\d{2}(:\d{2})?$/.test(val)) return val;
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return String(val);
    return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  };

  // Mobile view for each booking
  const MobileBookingCard = ({ row }) => (
    <div className="vendor-booking-mobile-card mb-3 p-3 border rounded">
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div className="fw-semibold">{row.booking_code || row.id}</div>
        <Badge bg={statusVariant(row.status)}>{statusLabel(row.status)}</Badge>
      </div>
      
      <div className="mb-2">
        <div className="fw-semibold">{row.contact_name || "-"}</div>
        <div className="text-muted small">{row.contact_phone || ""}</div>
      </div>

      <div className="mb-2">
        <div className="text-muted small">Services:</div>
        <div className="small">{(row.service_names || []).join(", ") || "-"}</div>
      </div>

      <div className="mb-2">
        <div className="text-muted small">Schedule:</div>
        <div className="fw-semibold">{formatDate(row.scheduled_date)}</div>
        <div className="text-muted small">{formatTime(row.scheduled_time)}</div>
      </div>

      <div className="row g-2 mb-2">
        <div className="col-6">
          <div className="text-muted small">Total:</div>
          <div className="fw-semibold">{row.currency || "INR"} {Number(row.total || 0).toFixed(2)}</div>
        </div>
        <div className="col-6">
          <div className="text-muted small">Commission:</div>
          <div className="fw-semibold text-danger">{row.currency || "INR"} {Number(row.admin_commission || 0).toFixed(2)}</div>
        </div>
        <div className="col-12">
          <div className="text-muted small">Your Payout:</div>
          <div className="fw-semibold text-primary">{row.currency || "INR"} {Number(row.vendor_payout ?? (Number(row.total || 0) - Number(row.admin_commission || 0))).toFixed(2)}</div>
        </div>
      </div>

      {Number(row.status) === 0 && (
        <div className="d-flex gap-2 mt-2">
          <Button
            size="sm"
            variant="success"
            disabled={updatingId === row.id}
            onClick={() => updateStatus(row.id, 1)}
            className="flex-grow-1"
          >
            {updatingId === row.id ? "..." : "Complete"}
          </Button>
          <Button
            size="sm"
            variant="danger"
            disabled={updatingId === row.id}
            onClick={() => updateStatus(row.id, 2)}
            className="flex-grow-1"
          >
            {updatingId === row.id ? "..." : "Cancel"}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="vendor-bookings-container">
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">Service Bookings</h4>
            <Button 
              variant="outline-primary" 
              className="d-md-none"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </div>
          
          <div className="d-flex flex-wrap gap-3 mb-3">
            <div className="px-3 py-2 rounded border bg-light">
              <div className="text-muted small">Your Earnings</div>
              <div className="fw-bold fs-5">
                {earningsSummary.currency} {earningsSummary.total.toFixed(2)}
              </div>
            </div>
          </div>

          {/* Filters - collapsible on mobile */}
          <div className={`vendor-filters ${showFilters ? 'show' : ''}`}>
            <Row className="g-3 align-items-end">
              <Col xs={12} md={3}>
                <Form.Label>Status</Form.Label>
                <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="">All</option>
                  <option value="0">Pending</option>
                  <option value="1">Completed</option>
                  <option value="2">Cancelled</option>
                </Form.Select>
              </Col>
              <Col xs={12} md={3}>
                <Form.Label>Date From</Form.Label>
                <Form.Control type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </Col>
              <Col xs={12} md={3}>
                <Form.Label>Date To</Form.Label>
                <Form.Control type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </Col>
              <Col xs={12} md={3}>
                <div className="d-flex gap-2">
                  <Button variant="primary" onClick={fetchBookings} className="flex-grow-1">
                    Apply
                  </Button>
                  <Button
                    variant="light"
                    onClick={() => {
                      setStatus("");
                      setDateFrom("");
                      setDateTo("");
                      setTimeout(fetchBookings, 0);
                    }}
                    className="flex-grow-1"
                  >
                    Reset
                  </Button>
                </div>
              </Col>
            </Row>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          {loading ? (
            <div className="d-flex align-items-center justify-content-center py-5">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading bookings...
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="vendor-table-view d-none d-md-block">
                <Table responsive hover className="align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Booking Code</th>
                      <th>Customer</th>
                      <th>Services</th>
                      <th>Schedule</th>
                      <th>Status</th>
                      <th>Total</th>
                      <th>Admin Commission</th>
                      <th>Your Payout</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center py-4">
                          No bookings found.
                        </td>
                      </tr>
                    ) : (
                      filteredRows.map((row) => (
                        <tr key={row.id}>
                          <td className="fw-semibold">{row.booking_code || row.id}</td>
                          <td>
                            <div className="fw-semibold">{row.contact_name || "-"}</div>
                            <div className="text-muted small">{row.contact_phone || ""}</div>
                          </td>
                          <td className="small">{(row.service_names || []).join(", ") || "-"}</td>
                          <td>
                            <div className="fw-semibold">{formatDate(row.scheduled_date)}</div>
                            <div className="text-muted small">{formatTime(row.scheduled_time)}</div>
                          </td>
                          <td>
                            <Badge bg={statusVariant(row.status)}>{statusLabel(row.status)}</Badge>
                          </td>
                          <td className="fw-semibold">
                            {row.currency || "INR"} {Number(row.total || 0).toFixed(2)}
                          </td>
                          <td className="fw-semibold text-danger">
                            {row.currency || "INR"} {Number(row.admin_commission || 0).toFixed(2)}
                          </td>
                          <td className="fw-semibold text-primary">
                            {row.currency || "INR"} {Number(row.vendor_payout ?? (Number(row.total || 0) - Number(row.admin_commission || 0))).toFixed(2)}
                          </td>
                          <td>
                            {Number(row.status) === 0 ? (
                              <div className="d-flex gap-2">
                                <Button
                                  size="sm"
                                  variant="success"
                                  disabled={updatingId === row.id}
                                  onClick={() => updateStatus(row.id, 1)}
                                >
                                  {updatingId === row.id ? "..." : "Complete"}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="danger"
                                  disabled={updatingId === row.id}
                                  onClick={() => updateStatus(row.id, 2)}
                                >
                                  {updatingId === row.id ? "..." : "Cancel"}
                                </Button>
                              </div>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Mobile Card View */}
              <div className="d-block d-md-none">
                {filteredRows.length === 0 ? (
                  <div className="text-center py-4">
                    No bookings found.
                  </div>
                ) : (
                  filteredRows.map((row) => (
                    <MobileBookingCard key={row.id} row={row} />
                  ))
                )}
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default VendorBookings;